/**
 * Cosa resta sulla mappa dopo i filtri.
 *
 * Stava dentro il componente, ed è il motivo per cui lo stesso difetto è
 * sopravvissuto a due giri di correzioni: la catena dei filtri non era
 * verificabile da nessuna parte, e l'unico modo di sapere se funzionava era
 * aprire la pagina e fidarsi di quello che si vedeva — che è esattamente ciò
 * che non funziona quando il difetto è «mostra troppo».
 *
 * Qui è una funzione pura, e `scripts/check-topology-filter.mjs` la verifica.
 */

export type FilterNode = {
  id: string
  name: string
  ci_class: string | null
  ci_category?: string | null
  mgmt_ip: string | null
}

export type FilterEdge = {
  source: string
  target: string
  rel_type: string
  /** Porta, protocollo e volumi: vedi `servicesOf`. */
  attributes?: Record<string, unknown> | null
}

export type FilterInput<N extends FilterNode, E extends FilterEdge> = {
  nodes: N[]
  edges: E[]
  /** I tipi di relazione ammessi. `null` significa «tutti». */
  relTypes: string[] | null
  /** Le classi **nascoste** dall'operatore. */
  hiddenClasses: Set<string>
  /** Il testo cercato, già ripulito. */
  query: string
  focus: string | null
  depth: number
  /** Chi sta dentro l'intorno di `focus` a `depth` salti. */
  neighbourhood: (focus: string, edges: E[], depth: number) => Set<string>
  /** La porta cercata, come testo. Vuoto significa «qualunque». */
  port?: string
  /** I protocolli ammessi. Vuoto significa «tutti». */
  protocols?: Set<string>
  /**
   * Da che parte va il traffico, **rispetto al nodo a fuoco**.
   *
   * Ha senso solo con una messa a fuoco: senza un punto di riferimento
   * «entrante» e «uscente» non significano niente — ogni arco è entrante per
   * uno dei due capi. Applicarlo comunque toglierebbe metà della mappa senza
   * un criterio che qualcuno possa spiegare.
   */
  direction?: 'any' | 'out' | 'in'
}

export type FilterResult<N, E> = {
  nodes: N[]
  edges: E[]
  /** Gli id che hanno **davvero** corrisposto alla ricerca. */
  matched: Set<string>
  /** Quanti nodi la messa a fuoco ha lasciato fuori. */
  hiddenByFocus: number
}

/** La classe di un nodo, con il ripiego che i filtri usano come chiave. */
export function classOf(node: FilterNode): string {
  return node.ci_class ?? 'unknown'
}

/**
 * Se un nodo corrisponde alla ricerca.
 *
 * Nome, **indirizzo di gestione** e classe: gli stessi campi che cerca il
 * Control Plane. Cercarne meno qui che là farebbe trovare in mappa cose che la
 * ricerca globale non trova, e viceversa — due risposte diverse alla stessa
 * domanda, nella stessa pagina.
 *
 * L'indirizzo in particolare è il modo in cui si cerca un apparato più spesso
 * del nome: di uno switch si ricorda l'IP di gestione, non l'hostname che gli
 * ha dato qualcun altro tre anni fa.
 */
export function matches(node: FilterNode, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return false
  return (
    node.name.toLowerCase().includes(needle) ||
    (node.mgmt_ip ?? '').toLowerCase().includes(needle) ||
    classOf(node).toLowerCase().includes(needle)
  )
}

/**
 * Applica i filtri, nell'ordine: famiglia → classe → ricerca → messa a fuoco.
 *
 * L'ordine non è indifferente. La ricerca viene **dopo** i filtri di famiglia e
 * classe perché cerca in ciò che si sta guardando; verrebbe prima, cercherebbe
 * anche fra le cose che l'operatore ha appena escluso, e la mappa mostrerebbe
 * di nuovo quello che era stato tolto.
 */
export function applyFilters<N extends FilterNode, E extends FilterEdge>(
  input: FilterInput<N, E>,
): FilterResult<N, E> {
  const { nodes, edges, relTypes, hiddenClasses, focus, depth } = input
  const query = input.query.trim()
  const byId = new Map(nodes.map((n) => [n.id, n]))

  const byFamily = relTypes === null ? edges : edges.filter((e) => relTypes.includes(e.rel_type))

  const visible = (id: string) => {
    const node = byId.get(id)
    return node ? !hiddenClasses.has(classOf(node)) : false
  }
  const byClass = byFamily.filter((e) => visible(e.source) && visible(e.target))

  // La ricerca **filtra**, non spegne.
  //
  // Prima i nodi che non corrispondevano restavano in mappa, più chiari: la
  // mappa mostrava tutto, e chi aveva scritto un indirizzo vedeva la stessa
  // nuvola di prima. «Filtro» vuol dire che quello che non serve non c'è.
  // Porta e protocollo: filtrano i **collegamenti**, non i nodi.
  //
  // Un collegamento che non porta la porta cercata sparisce; i nodi che
  // restano senza collegamenti spariscono di conseguenza. È l'unico ordine che
  // dà un risultato leggibile: filtrare i nodi lascerebbe archi che pendono nel
  // vuoto.
  const port = (input.port ?? '').trim()
  const protocols = input.protocols ?? new Set<string>()
  const byTraffic =
    !port && protocols.size === 0
      ? byClass
      : byClass.filter((e) => edgeCarries(e, port, protocols))

  const matched = new Set(query ? nodes.filter((n) => matches(n, query)).map((n) => n.id) : [])
  const bySearch = query
    ? byTraffic.filter((e) => matched.has(e.source) || matched.has(e.target))
    : byTraffic

  if (!focus || !byId.has(focus)) {
    const ids = new Set(bySearch.flatMap((e) => [e.source, e.target]))
    // Un nodo che corrisponde e non ha collegamenti visibili si mostra lo
    // stesso: dire «trovato, ma non te lo faccio vedere» è peggio del non
    // trovarlo.
    for (const id of matched) ids.add(id)
    return {
      nodes: nodes.filter((n) => ids.has(n.id)),
      edges: bySearch,
      matched,
      hiddenByFocus: 0,
    }
  }

  // La direzione si applica **qui**, dove un punto di riferimento c'è.
  const direction = input.direction ?? 'any'
  const byDirection =
    direction === 'any'
      ? bySearch
      : bySearch.filter((e) => (direction === 'out' ? e.source === focus : e.target === focus))

  const keep = input.neighbourhood(focus, byDirection, depth)
  const focused = byDirection.filter((e) => keep.has(e.source) && keep.has(e.target))
  const reachable = new Set(byDirection.flatMap((e) => [e.source, e.target]))
  return {
    nodes: nodes.filter((n) => keep.has(n.id)),
    edges: focused,
    matched,
    hiddenByFocus: Math.max(0, reachable.size - keep.size),
  }
}

// ---------------------------------------------------------------- traffico

/**
 * Un servizio osservato su un collegamento: porta, protocollo, volume.
 *
 * Le relazioni di traffico portano `services: [{port, name, protocol, bytes}]`
 * — una **lista**, perché fra due macchine passano più cose. Le adiacenze di
 * rete portano invece campi piatti. La console leggeva solo i campi piatti, e
 * per ogni collegamento di dipendenza non trovava niente: nessun errore,
 * nessuna etichetta, e la conclusione che il protocollo non fosse stato
 * raccolto.
 */
export type Service = {
  port: number | null
  protocol: string
  name: string
  bytes: number | null
}

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/** I servizi di un collegamento, da qualunque forma li porti. */
export function servicesOf(edge: { attributes?: Record<string, unknown> | null }): Service[] {
  const attrs = edge.attributes ?? {}

  const list = attrs.services
  if (Array.isArray(list)) {
    return list
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      .map((entry) => ({
        port: asNumber(entry.port),
        protocol: String(entry.protocol ?? '').toLowerCase(),
        name: String(entry.name ?? ''),
        bytes: asNumber(entry.bytes),
      }))
  }

  // La forma piatta: una sola coppia porta/protocollo sull'attributo.
  const protocol = String(attrs.protocol ?? '').toLowerCase()
  const port = asNumber(attrs.port)
  if (!protocol && port === null) return []
  return [{ port, protocol, name: String(attrs.name ?? ''), bytes: asNumber(attrs.bytes) }]
}

/** I protocolli distinti presenti su un insieme di collegamenti. */
export function protocolsIn(edges: { attributes?: Record<string, unknown> | null }[]): string[] {
  const found = new Set<string>()
  for (const edge of edges) for (const s of servicesOf(edge)) if (s.protocol) found.add(s.protocol)
  return [...found].sort()
}

/**
 * Come si legge un collegamento di traffico.
 *
 * `443/tcp` e non `tcp/443`: è l'ordine con cui lo scrivono i firewall e le
 * regole di sicurezza, ed è quello che chi guarda si aspetta di riconoscere.
 */
export function serviceLabel(service: Service): string {
  const address = service.port !== null ? `${service.port}/${service.protocol || '?'}` : service.protocol
  return service.name ? `${address} (${service.name})` : address
}

/**
 * Se un collegamento porta la porta **e** il protocollo cercati.
 *
 * Sullo **stesso** servizio, non su due servizi diversi. Valutarli separatamente
 * sembra equivalente e non lo è: un collegamento che porta 443/tcp e 53/udp
 * soddisferebbe «porta 443, protocollo UDP» pur non avendo mai visto 443/udp.
 * È una risposta plausibile e sbagliata — e su una domanda di sicurezza
 * («chi parla su questa porta con questo protocollo») è la peggiore delle due
 * cose.
 */
export function edgeCarries(
  edge: { attributes?: Record<string, unknown> | null },
  port: string,
  protocols: Set<string>,
): boolean {
  const wantedPort = port.trim()
  if (!wantedPort && protocols.size === 0) return true

  return servicesOf(edge).some((service) => {
    const portOk = !wantedPort || (service.port !== null && String(service.port) === wantedPort)
    const protocolOk = protocols.size === 0 || protocols.has(service.protocol)
    return portOk && protocolOk
  })
}
